import type { TimeRangePreset } from "@/core/components/ui/time-range-picker";
import { dayjs } from "@/core/utils/date";
/** Status values the profitability run API returns, and how each one reads. */
export const RUN_STATUS = {
  pending: "pending",
  running: "running",
  completed: "completed",
  failed: "failed",
} as const;

export type RunStatus = (typeof RUN_STATUS)[keyof typeof RUN_STATUS];

type StatusMeta = {
  label: string;
  variant: "gray" | "blue" | "green" | "red";
};

/**
 * Owner-facing wording. "Queued"/"Building" describe what the report is doing
 * rather than what the worker is doing, and "Ready" beats "Completed" for
 * something you are about to open.
 */
export const RUN_STATUS_META: Record<RunStatus, StatusMeta> = {
  [RUN_STATUS.pending]: { label: "Queued", variant: "gray" },
  [RUN_STATUS.running]: { label: "Building", variant: "blue" },
  [RUN_STATUS.completed]: { label: "Ready", variant: "green" },
  [RUN_STATUS.failed]: { label: "Failed", variant: "red" },
};

/** How often an unfinished run is re-checked. */
export const RUN_POLL_INTERVAL_MS = 2_000;

export const RUN_PAGE_SIZE = 25;

/**
 * The longest report the API will build, mirroring
 * `ProfitabilityReportRun::MAX_SPAN_DAYS`. Counted inclusively, so a range of
 * exactly this many days is allowed. Keep the two in step: a looser rule here
 * sends a request the server is certain to refuse.
 */
export const MAX_REPORT_SPAN_DAYS = 731;

/**
 * The ranges owners actually ask a profitability report for. Narrower than the
 * picker's defaults, which start at "this week" — a week of jobs is rarely the
 * question, and a year is the one people forget they can ask for.
 */
export const REPORT_PERIOD_PRESETS: TimeRangePreset[] = [
  {
    label: "Last month",
    getValue: () => ({
      from: dayjs().subtract(1, "month").startOf("month").toDate(),
      to: dayjs().subtract(1, "month").endOf("month").toDate(),
      label: "Last month",
    }),
  },
  {
    label: "This month",
    getValue: () => ({
      from: dayjs().startOf("month").toDate(),
      to: dayjs().toDate(),
      label: "This month",
    }),
  },
  {
    label: "Last 30 days",
    getValue: () => ({
      from: dayjs().subtract(29, "day").toDate(),
      to: dayjs().toDate(),
      label: "Last 30 days",
    }),
  },
  {
    label: "Last 90 days",
    getValue: () => ({
      from: dayjs().subtract(89, "day").toDate(),
      to: dayjs().toDate(),
      label: "Last 90 days",
    }),
  },
  {
    label: "Year to date",
    getValue: () => ({
      from: dayjs().startOf("year").toDate(),
      to: dayjs().toDate(),
      label: "Year to date",
    }),
  },
];
